import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
} from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useAppThemeContext, useAuthContext, useDrawerContext } from '../../contexts';
import { Environment } from '../../environment';
import { THEME_OPTIONS, radii } from '../../themes';
import { DialogPefilFoto } from './DialogPefilFoto';
import { DialogPefil } from './DialogPerfil';
import { UsuarioService } from '../../services/api/cadastros/UsuariosService';
import { layoutTokens, molduraDeFundoSx, SidebarItem, SubmenuPanel } from '../app-shell';
import { useOffline } from '../../offline/useOffline';
import { iniciarSincronizacaoAutomatica } from '../../offline/sincronizacao';
import { PainelSincronizacao } from '../../offline/PainelSincronizacao';
import { formaInstalacao, inscreverInstalacao, instalarApp } from '../../pwa/instalacao';
import { DialogInstalarIos } from '../../pwa/DialogInstalarIos';
import { AppMobile } from './AppMobile';
import { AvisoCertificadoLogin, IconeAlertaCertificado } from '../certificado/AvisoCertificado';

// Chaves internas do painel de Configurações (não são rotas): o "módulo" em
// openModule e o item que abre o PainelSincronizacao em vez de navegar.
const MODULO_CONFIGURACOES = '__configuracoes';
const ITEM_SINCRONIZACAO = '__sincronizacao';
const ITEM_INSTALAR = '__instalar';

const SIDEBAR_STORAGE_KEY = 'APP_SIDEBAR_EXPANDED';

// Usado por telas de loading em tela cheia para deixar o rail do menu de fora da área
// coberta (ver TelaCarregando.tsx) — corresponde à largura mínima da sidebar (modo recolhido).
export const MENU_LATERAL_RAIL_WIDTH = layoutTokens.sidebarClosed;

const getBaseRoute = (path: string) => {
  const parts = path.split('/').filter(Boolean);
  return parts[0] || '';
};

// Mosaico low-poly do fundo da sidebar: grade de pontos com deslocamento
// aleatório, cada célula dividida em 2 triângulos com tons um pouco
// diferentes e contorno bem fino. Gerado uma vez (SVG em data URI, sem
// imagem externa) com semente fixa — o desenho é sempre o mesmo, não muda a
// cada carregamento. Existe uma paleta pra sidebar escura (teal/ciano) e
// outra pro menu claro (azul do tema claro sobre branco).
interface IPaletaMosaico {
  tomPrincipal: string; // "r,g,b"
  tomDestaque: string; // "r,g,b" — usado em ~30% dos triângulos
  contorno: string; // cor completa, ex: rgba(...)
  opacidadeMinima: number;
  opacidadeVariacao: number;
}

const LARGURA_MOSAICO = 280;

const gerarMosaico = (paleta: IPaletaMosaico) => {
  const largura = LARGURA_MOSAICO;
  const altura = 1000;
  const colunas = 4;
  const linhas = 13;

  // PRNG simples (mulberry32) — determinístico pela semente, então as duas
  // paletas desenham exatamente a mesma malha de triângulos.
  let semente = 20260926;
  const aleatorio = () => {
    semente = (semente + 0x6d2b79f5) | 0;
    let t = Math.imul(semente ^ (semente >>> 15), 1 | semente);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const passoX = largura / colunas;
  const passoY = altura / linhas;
  // Pontos da borda ficam presos na borda (só os internos se deslocam),
  // pra o mosaico cobrir o retângulo inteiro sem buracos.
  const pontos = Array.from({ length: linhas + 1 }, (_, l) => Array.from({ length: colunas + 1 }, (_, c) => {
    const internoX = c > 0 && c < colunas;
    const internoY = l > 0 && l < linhas;
    return [
      c * passoX + (internoX ? (aleatorio() - 0.5) * passoX * 0.7 : 0),
      l * passoY + (internoY ? (aleatorio() - 0.5) * passoY * 0.7 : 0),
    ];
  }));

  const triangulo = (a: number[], b: number[], c: number[]) => {
    const centroY = (a[1] + b[1] + c[1]) / 3;
    // Mais forte no topo, apagando pra baixo; variação por triângulo dá o
    // efeito de facetas.
    const opacidade = (paleta.opacidadeMinima + aleatorio() * paleta.opacidadeVariacao) * (1 - (centroY / altura) * 0.45);
    const tom = aleatorio() < 0.3 ? paleta.tomDestaque : paleta.tomPrincipal;
    const p = [a, b, c].map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    return `<polygon points="${p}" fill="rgba(${tom},${opacidade.toFixed(3)})" stroke="${paleta.contorno}" stroke-width="0.6"/>`;
  };

  const poligonos: string[] = [];
  for (let l = 0; l < linhas; l++) {
    for (let c = 0; c < colunas; c++) {
      const a = pontos[l][c];
      const b = pontos[l][c + 1];
      const d = pontos[l + 1][c];
      const e = pontos[l + 1][c + 1];
      // Alterna a diagonal da célula pra não formar um padrão "xadrez" óbvio.
      if ((l + c) % 2 === 0) {
        poligonos.push(triangulo(a, b, e), triangulo(a, e, d));
      } else {
        poligonos.push(triangulo(a, b, d), triangulo(b, e, d));
      }
    }
  }

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${largura} ${altura}" preserveAspectRatio="none">${poligonos.join('')}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const MOSAICO_ESCURO = gerarMosaico({
  tomPrincipal: '22,150,160',
  tomDestaque: '66,242,247',
  contorno: 'rgba(66,242,247,0.07)',
  opacidadeMinima: 0.025,
  opacidadeVariacao: 0.075,
});

// Menu claro: azul do tema claro (#0B7C86) e um cinza-azulado, bem leves
// sobre o branco — só o suficiente pra aparecer a textura.
const MOSAICO_CLARO = gerarMosaico({
  tomPrincipal: '100,116,139',
  tomDestaque: '2,132,199',
  contorno: 'rgba(11, 124, 134,0.08)',
  opacidadeMinima: 0.015,
  opacidadeVariacao: 0.05,
});

// Versões bem leves (a pedido) — usadas com o fundo `liso`: só uma textura
// sutil, ~1/3 da intensidade das versões acima e contornos quase invisíveis.
const MOSAICO_ESCURO_LEVE = gerarMosaico({
  tomPrincipal: '22,150,160',
  tomDestaque: '66,242,247',
  contorno: 'rgba(66,242,247,0.025)',
  opacidadeMinima: 0.008,
  opacidadeVariacao: 0.025,
});
const MOSAICO_CLARO_LEVE = gerarMosaico({
  tomPrincipal: '100,116,139',
  tomDestaque: '2,132,199',
  contorno: 'rgba(11, 124, 134,0.03)',
  opacidadeMinima: 0.006,
  opacidadeVariacao: 0.018,
});

// Mais presente no topo (logo) e no rodapé, bem calmo no meio, atrás da
// lista de módulos, pra não brigar com o texto.
const MASCARA_MOSAICO = 'linear-gradient(180deg, #000 0%, rgba(0,0,0,0.55) 30%, rgba(0,0,0,0.35) 60%, rgba(0,0,0,0.8) 100%)';

const ElegantDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== 'menucolor' && prop !== 'menuborder' && prop !== 'mosaico' && prop !== 'railwidth' && prop !== 'liso',
})<{ menucolor: string; menuborder: string; mosaico: boolean; railwidth: number; liso?: boolean }>(({ menuborder, mosaico, railwidth, liso, theme }) => ({
  width: railwidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: railwidth,
    overflowX: 'hidden',
    border: 'none',
    borderRight: `1px solid ${menuborder}`,
    // Só arredonda o lado visível (direito) — esquerdo/topo/rodapé ficam
    // colados na borda da tela, então não têm canto pra arredondar.
    // `overflow: hidden` corta o mosaico (abaixo) nesse arredondado.
    borderTopRightRadius: radii.xl,
    borderBottomRightRadius: radii.xl,
    overflow: 'hidden',
    // Garante que ::before/::after (mosaico) fiquem sempre atrás do
    // conteúdo (Box do MenuLateral, ver JSX) mesmo sem precisar empilhar
    // z-index alto — sem isso, dependendo da ordem de paint, o mosaico
    // podia vazar por cima do texto em navegadores mais antigos.
    isolation: 'isolate',
    // Não seta `position` aqui — o MUI já deixa .MuiDrawer-paper como
    // `position: fixed` (pros dois variants, inclusive o "docked" do modo
    // desktop); sobrescrever pra 'relative' quebraria esse pin e o overlay
    // do variant="temporary" usado no mobile. `fixed` já é "positioned" o
    // suficiente pra servir de referência aos ::before/::after abaixo.
    transition: theme.transitions.create('width', { duration: 200 }),
    // `mosaico` = sidebar escura (temas 'light' e 'dark'); false = menu
    // claro ('lightWhiteMenu'), com fundo branco e o mosaico claro.
    // `liso` (padrão em todos os temas, a pedido): fundo calmo — só um
    // degradê bem suave (escuro ou claro), sem mosaico nem brilhos, que
    // deixavam o menu carregado demais.
    background: liso
      ? (mosaico
        ? `linear-gradient(180deg, ${theme.torq.sidebar.bg} 0%, ${theme.torq.sidebar.submenuBg} 100%)`
        : 'linear-gradient(180deg, #FFFFFF 0%, #F7FAFC 100%)')
      : mosaico
      ? `
        radial-gradient(circle at 20% 15%, rgba(8, 74, 78, 0.18), transparent 35%),
        radial-gradient(circle at 90% 80%, rgba(0, 120, 125, 0.10), transparent 30%),
        linear-gradient(180deg, #03262d 0%, #021f27 45%, #011820 100%)
      `
      : 'linear-gradient(180deg, #FFFFFF 0%, #F8FBFD 55%, #F1F6FA 100%)',
    boxShadow: mosaico ? 'inset -1px 0 rgba(255,255,255,0.03)' : 'none',
    // Camada 1: o mosaico em si. Largura fixa e altura esticada até o fim
    // da sidebar — no modo recolhido (rail) só aparece a faixa da esquerda,
    // sem espremer os triângulos.
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      pointerEvents: 'none',
      backgroundImage: liso
        ? (mosaico ? MOSAICO_ESCURO_LEVE : MOSAICO_CLARO_LEVE)
        : (mosaico ? MOSAICO_ESCURO : MOSAICO_CLARO),
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'left top',
      backgroundSize: `${LARGURA_MOSAICO}px 100%`,
      maskImage: MASCARA_MOSAICO,
      WebkitMaskImage: MASCARA_MOSAICO,
    },
    // Camada 2: brilhos bem suaves nos cantos, na cor de destaque de cada
    // variante — dão profundidade e amarram o mosaico com o tema.
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      zIndex: 1,
      pointerEvents: 'none',
      display: liso ? 'none' : 'block',
      background: mosaico
        ? `
          radial-gradient(120% 40% at 0% 0%, rgba(66, 242, 247, 0.10), transparent 60%),
          radial-gradient(90% 30% at 100% 100%, rgba(66, 242, 247, 0.07), transparent 65%)
        `
        : `
          radial-gradient(120% 40% at 0% 0%, rgba(11, 124, 134, 0.07), transparent 60%),
          radial-gradient(90% 30% at 100% 100%, rgba(11, 124, 134, 0.05), transparent 65%)
        `,
    },
  },
}));

interface IMenuLateralProps {
  children: React.ReactNode;
}

export const MenuLateral: React.FC<IMenuLateralProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  // Telas baixas (janela pouco alta, mesmo que larga) não são cobertas pelos breakpoints
  // de largura acima — sem isso a sidebar (logo + avatar + módulos + rodapé) estoura a altura
  // da viewport e força scroll interno na lista de módulos. Existem sempre 5 módulos (ver
  // drawerOptions em routes/index.tsx), por isso os tamanhos abaixo foram calculados para os
  // 5 caberem sem scroll até telas bem baixas (~360px de altura).
  const shortH = useMediaQuery('(max-height:760px)');
  const veryShortH = useMediaQuery('(max-height:620px)');
  const ultraShortH = useMediaQuery('(max-height:500px)');

  const avatarSize = ultraShortH ? 36 : veryShortH ? 44 : shortH ? 56 : 56;
  const itemHeight = ultraShortH ? 36 : veryShortH ? 40 : shortH ? 44 : 48;
  const itemIconSize = ultraShortH ? '1.15rem' : veryShortH ? '1.25rem' : '1.4rem';
  const modulesGap = ultraShortH ? 0.3 : veryShortH ? 0.5 : 0.75;

  const [openModule, setOpenModule] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElTema, setAnchorElTema] = useState<null | HTMLElement>(null);
  const [openFoto, setOpenFoto] = useState(false);
  const [openPerfil, setOpenPerfil] = useState(false);
  const [fotoPerfil, setFotoPerfil] = useState('');
  const [idUser, setIdUser] = useState('');
  const [nomeUsuario, setNomeUsuario] = useState('');
  const [loginUsuario, setLoginUsuario] = useState('');
  const openMenu = Boolean(anchorEl);
  const openMenuTema = Boolean(anchorElTema);

  // Computador: o SIAC sempre abre com o menu expandido. Recolher vale só
  // enquanto o sistema estiver aberto (sessionStorage — sobrevive ao F5, mas
  // não a fechar e abrir o sistema de novo). Antes ficava gravado pra sempre
  // no localStorage, e quem recolheu uma vez abria sempre recolhido.
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      localStorage.removeItem(SIDEBAR_STORAGE_KEY);
      return sessionStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(SIDEBAR_STORAGE_KEY, String(sidebarExpanded));
    } catch {
      // Preferência é best-effort.
    }
  }, [sidebarExpanded]);

  const { isDrawerOpen, toggleDrawerOpen, drawerOptions, drawerItemOptions } = useDrawerContext();
  const { themeName, setThemeName } = useAppThemeContext();
  const { logout } = useAuthContext();

  // Sistema offline (ver shared/offline): o menu só existe com alguém
  // logado, então é daqui que a sincronização automática começa — envia o
  // que foi lançado sem internet e baixa a cópia local dos cadastros.
  useEffect(() => iniciarSincronizacaoAutomatica(), []);
  const offline = useOffline();
  const [openSincronizacao, setOpenSincronizacao] = useState(false);
  // "Instalar SIAC neste aparelho" — no computador/Android só aparece quando o
  // navegador oferece a instalação; no iPhone/iPad aparece sempre que não
  // estiver instalado e abre o passo a passo do Safari (ver
  // shared/pwa/instalacao.ts).
  const [formaInstalar, setFormaInstalar] = useState(formaInstalacao);
  useEffect(() => inscreverInstalacao(() => setFormaInstalar(formaInstalacao())), []);
  const [openInstalarIos, setOpenInstalarIos] = useState(false);

  // No mobile o Drawer é temporário (só existe na tela enquanto aberto), então não faz
  // sentido ficar em modo rail (ícone sem rótulo) lá dentro — mostra o nome do módulo
  // junto do ícone sempre que aberto. Recolher/expandir (o botão de chevron) continua
  // reservado ao desktop, onde a sidebar fica fixa e o rail existe pra economizar espaço.
  const expanded = smDown || sidebarExpanded;
  const railWidth = smDown ? layoutTokens.sidebarOpen : (sidebarExpanded ? layoutTokens.sidebarOpen : layoutTokens.sidebarClosed);

  // Nome no cartão da sidebar: só os dois primeiros nomes (ex.: "Eduardo
  // Santos" em vez de "Eduardo Santos De Souza") — cabe folgado em 1-2
  // linhas sem precisar decidir se o nome completo cabe ou não.
  const nomeSidebarExibido = nomeUsuario.trim().split(/\s+/).slice(0, 2).join(' ');

  // O menu é escuro nos dois temas (logo branca, mosaico escuro).
  const sidebarEscura = true;

  const handleToggleSidebar = () => {
    if (smDown) {
      toggleDrawerOpen();
    } else {
      setSidebarExpanded((old) => !old);
    }
  };

  interface JwtPayload {
    iduser: string;
    img: string;
  }

  useEffect(() => {
    const token = localStorage.getItem('APP_ACCESS_TOKEN');
    if (token) {
      const decoded = jwtDecode<JwtPayload>(token);
      setFotoPerfil(Environment.URL_IMGUSUARIOS + decoded.img);
      setIdUser(decoded.iduser);
    }
  }, []);

  useEffect(() => {
    setOpenModule(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!idUser) return;
    UsuarioService.getByIdPerfil(idUser).then((result) => {
      if (!(result instanceof Error)) {
        setNomeUsuario(result.nome);
        setLoginUsuario(result.login);
      }
    });
  }, [idUser]);

  const currentBase = getBaseRoute(location.pathname);

  const isModuleActive = (modulePath: string) => {
    if (getBaseRoute(modulePath) === currentBase) return true;
    return drawerItemOptions.some(
      (item) => item.parent === modulePath && getBaseRoute(item.path) === currentBase
    );
  };

  const handleModuleClick = (modulePath: string, hasSubItems: boolean) => {
    if (hasSubItems) {
      setOpenModule((old) => (old === modulePath ? null : modulePath));
    } else {
      navigate(modulePath);
      setOpenModule(null);
      if (smDown) toggleDrawerOpen();
    }
  };

  const handleNavigateSubItem = (path: string) => {
    if (path === ITEM_SINCRONIZACAO) {
      setOpenModule(null);
      setOpenSincronizacao(true);
      return;
    }
    if (path === ITEM_INSTALAR) {
      setOpenModule(null);
      if (formaInstalar === 'ios') setOpenInstalarIos(true);
      else instalarApp();
      return;
    }
    navigate(path);
    setOpenModule(null);
    if (smDown) toggleDrawerOpen();
  };

  // "Configurações" abre o mesmo painel de submenu dos módulos — é um
  // "módulo" fixo do rodapé da sidebar, não vem de drawerOptions: dados da
  // empresa + status de conexão/sincronização (antes um item próprio,
  // sempre visível na sidebar). O item de conexão não navega, abre o
  // PainelSincronizacao (ver handleNavigateSubItem).
  const configuracoesAberto = openModule === MODULO_CONFIGURACOES;
  const statusConexao = `${offline.sincronizacao.sincronizando ? 'Sincronizando' : offline.online ? 'Online' : 'Offline'}${offline.pendentes > 0 ? ` • ${offline.pendentes} pendente${offline.pendentes > 1 ? 's' : ''}` : ''}`;
  const itensConfiguracoes = [
    {
      icon: 'business' as const,
      label: 'Configurações da empresa',
      descricao: 'Dados da empresa e parâmetros do sistema',
      path: '/empresa',
      active: getBaseRoute(location.pathname) === 'empresa',
    },
    {
      icon: offline.sincronizacao.sincronizando ? 'cloudSync' as const : offline.online ? 'cloudDone' as const : 'cloudOff' as const,
      label: 'Conexão e sincronização',
      descricao: statusConexao,
      path: ITEM_SINCRONIZACAO,
      active: false,
    },
    ...(formaInstalar ? [{
      icon: 'installDesktop' as const,
      label: 'Instalar SIAC neste aparelho',
      descricao: 'Cria um ícone e abre o sistema mesmo sem internet',
      path: ITEM_INSTALAR,
      active: false,
    }] : []),
  ];

  const activeModuleOption = drawerOptions.find((d) => d.path === openModule);
  const activeSubItems = useMemo(
    () =>
      drawerItemOptions
        .filter((i) => i.parent === openModule)
        .map((sub) => ({
          icon: sub.icon,
          label: sub.label,
          descricao: sub.descricao,
          path: sub.path,
          active: getBaseRoute(location.pathname) === getBaseRoute(sub.path),
        })),
    [drawerItemOptions, openModule, location.pathname]
  );


  // Celular: sem menu lateral — navegação no estilo app de banco (barra fixa
  // embaixo + gavetas), ver AppMobile.tsx. Fica depois de todos os hooks
  // acima (regra dos hooks); a sincronização automática continua iniciando
  // aqui nos dois modos.
  if (smDown) {
    return (
      <>
        <AppMobile
          nomeUsuario={nomeUsuario}
          loginUsuario={loginUsuario}
          fotoPerfil={fotoPerfil}
          onAbrirPerfil={() => setOpenPerfil(true)}
          onAbrirFoto={() => setOpenFoto(true)}
        >
          {children}
        </AppMobile>
        <DialogPefilFoto id={idUser} img={fotoPerfil} value="" open={openFoto} onClose={() => setOpenFoto(false)} />
        <DialogPefil id={idUser} value="" open={openPerfil} onClose={() => setOpenPerfil(false)} />
        <AvisoCertificadoLogin />
      </>
    );
  }

  return (
    <>
      <ClickAwayListener
        onClickAway={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest?.('[data-rail-button]')) return;
          setOpenModule(null);
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <ElegantDrawer
            open={isDrawerOpen}
            variant={smDown ? 'temporary' : 'permanent'}
            onClose={toggleDrawerOpen}
            menucolor={theme.torq.sidebar.bg}
            menuborder={theme.torq.sidebar.border}
            mosaico={sidebarEscura}
            liso
            railwidth={railWidth}
          >
            {/* zIndex acima do mosaico (::before/::after, zIndex 1) — logo,
              perfil, itens de menu e rodapé sempre por cima, nunca perdendo
              contraste pro fundo decorativo. */}
            <Box height="100%" display="flex" flexDirection="column" sx={{ position: 'relative', zIndex: 2 }}>
              {/* Logo no topo só com o menu expandido — recolhido fica só o
                  avatar (e o botão flutuante pra expandir, ver mais abaixo). */}
              {expanded && (
              <Box
                sx={{
                  height: layoutTokens.logoHeaderHeight,
                  minHeight: layoutTokens.logoHeaderHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: expanded ? 'space-between' : 'center',
                  gap: 1.25,
                  px: expanded ? 2.5 : 0,
                  // Sem cor própria aqui (nas duas variantes): deixa o mosaico
                  // (::before/::after do ElegantDrawer, atrás desta Box)
                  // aparecer por trás igual no resto do menu, em vez de uma
                  // faixa sólida cobrindo o topo.
                  backgroundColor: 'transparent',
                }}
              >
                {/* Logo clicável também no mobile: com o menu aberto, ela substitui o
                    botão de sanduíche (que desaparece, ver mais abaixo) como o controle
                    pra fechar o drawer — sem precisar de um ícone de chevron extra ali. */}
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1.25}
                  minWidth={0}
                  onClick={handleToggleSidebar}
                  sx={{ cursor: 'pointer' }}
                >
                  {expanded ? (
                    // Wordmark completa (glifo + "SIAC" já desenhados juntos)
                    // — troca de tom conforme a sidebar for escura ou clara.
                    <Box
                      component="img"
                      src={sidebarEscura ? '/img/siac-wordmark-white.png' : '/img/siac-wordmark-black.png'}
                      alt="SIAC"
                      sx={{ height: 38, width: 'auto', flexShrink: 0 }}
                    />
                  ) : (
                    <Box
                      component="img"
                      src="/img/siac-logo.png"
                      alt="SIAC"
                      sx={{ width: 30, height: 30, borderRadius: '6px', flexShrink: 0 }}
                    />
                  )}
                </Box>

                {!smDown && expanded && (
                  <IconButton size="small" onClick={handleToggleSidebar} sx={{ color: theme.torq.sidebar.textSecondary, flexShrink: 0 }}>
                    <ChevronLeftRounded fontSize="small" />
                  </IconButton>
                )}
              </Box>
              )}

              <Box sx={{ pt: expanded ? 0.75 : 2, pb: 1.5, px: expanded ? 1.25 : 1, width: '100%' }}>
                {/* Cartão horizontal (avatar + nome/login) no modo expandido —
                    no modo rail (ícone só) fica só o avatar centralizado,
                    sem cartão, não tem espaço pro texto. */}
                <Box
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: expanded ? 'flex-start' : 'center',
                    gap: 1.1,
                    cursor: 'pointer',
                    ...(expanded && !veryShortH
                      ? {
                        p: 1,
                        borderRadius: `${radii.md}px`,
                        bgcolor: theme.torq.sidebar.hover,
                        border: `1px solid ${theme.torq.sidebar.border}`,
                        transition: 'border-color 0.15s ease',
                        '&:hover': { borderColor: theme.torq.sidebar.activeAccentBar },
                      }
                      : {}),
                  }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                    {/* Certificado digital vencido/com problema: alerta no canto
                        do avatar — clicar explica (não abre o menu do usuário). */}
                    <Box sx={{ position: 'absolute', top: -8, left: -8, zIndex: 2 }}>
                      <IconeAlertaCertificado tamanho={18} />
                    </Box>
                    <Avatar
                      src={fotoPerfil}
                      sx={{
                        width: avatarSize,
                        height: avatarSize,
                        // Borda usava um ciano fixo (pensado só pra sidebar
                        // escura) — trocado pelo token de borda do tema, que
                        // já se adapta às três variantes (escuro, claro/menu
                        // escuro, claro/menu claro). Sombra suave dá um
                        // pouco de profundidade ao avatar em qualquer uma
                        // delas.
                        border: `2px solid ${theme.torq.sidebar.border}`,
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.18)',
                      }}
                    />
                    {/* Indicador de status online (spec do menu lateral) — círculo
                        recortado da própria cor de fundo da sidebar, pra parecer
                        "vazado" em vez de sobreposto. */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 2,
                        bottom: 2,
                        width: avatarSize <= 36 ? 8 : 10,
                        height: avatarSize <= 36 ? 8 : 10,
                        borderRadius: '50%',
                        bgcolor: '#48C62F',
                        border: `2px solid ${theme.torq.sidebar.bg}`,
                      }}
                    />
                  </Box>

                  {expanded && !veryShortH && (
                    <Box sx={{ minWidth: 0 }}>
                      <Tooltip title={nomeUsuario || loginUsuario}>
                        <Typography
                          fontWeight={700}
                          sx={{
                            lineHeight: 1.2,
                            fontSize: '0.8rem',
                            color: theme.torq.sidebar.textPrimary,
                            wordBreak: 'break-word',
                          }}
                        >
                          {nomeSidebarExibido || 'Usuário'}
                        </Typography>
                      </Tooltip>
                      {!!loginUsuario && (
                        <Typography
                          noWrap
                          sx={{ mt: 0.25, fontSize: '0.68rem', color: theme.torq.sidebar.textSecondary }}
                        >
                          {loginUsuario}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 220, borderRadius: `${radii.lg}px` } } }}
              >
                {/* Cabeçalho com foto grande + nome completo (sem quebra
                    forçada — aqui, diferente do cartão da sidebar, sobra
                    largura de sobra pro nome) + login. */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, pt: 1.5, pb: 1.5 }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <Avatar
                      src={fotoPerfil}
                      sx={{
                        width: 84,
                        height: 84,
                        border: `3px solid ${theme.torq.sidebar.activeAccentBar}`,
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 4,
                        bottom: 4,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: '#48C62F',
                        border: `2px solid ${theme.palette.background.paper}`,
                      }}
                    />
                  </Box>
                  <Typography fontWeight={700} align="center" sx={{ mt: 1.5, fontSize: '0.95rem', color: theme.palette.text.primary }}>
                    {nomeUsuario || 'Usuário'}
                  </Typography>
                  {!!loginUsuario && (
                    <Typography align="center" sx={{ fontSize: '0.78rem', fontWeight: 600, color: theme.torq.sidebar.activeAccentBar, mt: 0.25 }}>
                      {loginUsuario}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ mb: 0.5 }} />

                <MenuItem
                  onClick={() => {
                    setOpenFoto(true);
                    setAnchorEl(null);
                  }}
                >
                  <PhotoCameraRounded fontSize="small" sx={{ mr: 1 }} />
                  Mudar foto
                </MenuItem>

                <MenuItem
                  onClick={() => {
                    setOpenPerfil(true);
                    setAnchorEl(null);
                  }}
                >
                  <LockRounded fontSize="small" sx={{ mr: 1 }} />
                  Alterar senha
                </MenuItem>
              </Menu>

              <Box
                flex={1}
                display="flex"
                flexDirection="column"
                gap={modulesGap}
                sx={{
                  py: 1,
                  // Sem padding à esquerda no modo expandido: o marcador do
                  // item ativo precisa encostar na borda esquerda da sidebar,
                  // sem espaço sobrando entre os dois (o respiro do ícone/
                  // rótulo já vem do padding interno do próprio SidebarItem).
                  pl: expanded ? 0 : 1,
                  pr: expanded ? 1.25 : 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: '100%',
                  minHeight: 0,
                  scrollbarWidth: 'thin',
                  // Cor própria da sidebar (fundo escuro) — sem isso valeria a
                  // barra global do tema, pensada pro fundo das páginas.
                  scrollbarColor: `${theme.torq.sidebar.scrollbarThumb} transparent`,
                  '&:hover': { scrollbarColor: `${theme.torq.sidebar.scrollbarThumb} transparent` },
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.torq.sidebar.scrollbarThumb,
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                }}
              >
                {drawerOptions.map((drawer) => {
                  const hasSubItems = drawerItemOptions.some((i) => i.parent === drawer.path);
                  const active = isModuleActive(drawer.path) || openModule === drawer.path;

                  return (
                    <SidebarItem
                      key={drawer.path}
                      railButton
                      icon={drawer.icon}
                      label={drawer.label}
                      active={active}
                      expanded={expanded}
                      hasChevron={hasSubItems}
                      height={itemHeight}
                      iconSize={itemIconSize}
                      onClick={() => handleModuleClick(drawer.path, hasSubItems)}
                    />
                  );
                })}
              </Box>

              <Divider sx={{ width: '80%', mx: 'auto', mb: 1, borderColor: theme.torq.sidebar.divider }} />

              <Box
                display="flex"
                flexDirection="column"
                gap={modulesGap}
                sx={{ pb: 2, pl: expanded ? 0 : 1, pr: expanded ? 1.25 : 1 }}
              >
                <SidebarItem
                  railButton
                  icon="settings"
                  label="Configurações"
                  expanded={expanded}
                  height={itemHeight}
                  iconSize={itemIconSize}
                  hasChevron
                  active={getBaseRoute(location.pathname) === 'empresa' || configuracoesAberto}
                  onClick={() => handleModuleClick(MODULO_CONFIGURACOES, true)}
                />

                <SidebarItem
                  icon={<PaletteRounded />}
                  label="Tema"
                  expanded={expanded}
                  height={itemHeight}
                  iconSize={itemIconSize}
                  onClick={(e) => setAnchorElTema(e.currentTarget)}
                />

                <Menu anchorEl={anchorElTema} open={openMenuTema} onClose={() => setAnchorElTema(null)}>
                  {THEME_OPTIONS.map((opt) => (
                    <MenuItem
                      key={opt.name}
                      selected={themeName === opt.name}
                      onClick={() => {
                        setThemeName(opt.name);
                        setAnchorElTema(null);
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: opt.swatch,
                          mr: 1.5,
                          border: '1px solid rgba(0,0,0,0.15)',
                        }}
                      />
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>

                <SidebarItem
                  icon="exit_to_app"
                  label="Sair do sistema"
                  expanded={expanded}
                  height={itemHeight}
                  iconSize={itemIconSize}
                  onClick={logout}
                />
              </Box>
            </Box>
          </ElegantDrawer>

          {/* Menu recolhido: botão flutuante preso na borda do menu (metade
              pra fora), pra expandir de novo. */}
          {!smDown && !sidebarExpanded && (
            <Tooltip title="Expandir menu" placement="right">
              <IconButton
                onClick={handleToggleSidebar}
                aria-label="Expandir menu"
                size="small"
                sx={{
                  // Quase todo pra fora do menu (só encosta na borda), em tom
                  // neutro — discreto, não compete com o conteúdo da página.
                  position: 'fixed',
                  top: 22,
                  left: railWidth - 6,
                  zIndex: theme.zIndex.drawer + 3,
                  width: 26,
                  height: 26,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                  transition: 'color 150ms ease, border-color 150ms ease, background-color 150ms ease',
                  '&:hover': { bgcolor: 'background.paper', color: 'text.primary', borderColor: theme.palette.text.secondary },
                }}
              >
                <ChevronRightRounded sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <PainelSincronizacao open={openSincronizacao} onClose={() => setOpenSincronizacao(false)} />
          <DialogInstalarIos open={openInstalarIos} onClose={() => setOpenInstalarIos(false)} />

          <SubmenuPanel
            open={configuracoesAberto || (Boolean(openModule) && !!activeModuleOption)}
            left={railWidth}
            title={configuracoesAberto ? 'Configurações' : activeModuleOption?.label}
            items={configuracoesAberto ? itensConfiguracoes : activeSubItems}
            onItemClick={handleNavigateSubItem}
          />
        </Box>
      </ClickAwayListener>

      {/* CORREÇÃO: removido o botão flutuante (position:'fixed') que ficava sobreposto
          ao conteúdo — cada um dos 7 layouts de página (LayoutBaseDePagina,
          LayoutBaseDePaginaNfe, LayoutBaseDeFinanceiro(Conciliacao), LayoutBaseDeFolha,
          LayoutBaseDeDashboard, LayoutBaseDePaginaColaborador) já renderiza seu próprio
          botão de menu dentro do fluxo normal da página (não flutuante) quando smDown,
          então o botão fixo global aqui era redundante e dava a impressão de "flutuar"
          por cima do conteúdo. */}

      <Box
        sx={{
          marginLeft: smDown ? 0 : `${railWidth}px`,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'margin-left 200ms ease',
          // Contém qualquer conteúdo largo demais (ex.: tabela com muitas
          // colunas) dentro da própria área de conteúdo — sem isso, um
          // descendente mais largo que a tela empurra a página inteira e cria
          // um scroll horizontal duplicado (o da página + o do componente
          // largo). Componentes que realmente precisam rolar na horizontal
          // (TableContainer, por ex.) continuam rolando normalmente por
          // dentro, isso só evita o vazamento pro body.
          overflowX: 'hidden',
        }}
      >
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: 'background.default',
            ...molduraDeFundoSx(theme, railWidth),
          }}
        >
          {children}
        </Box>
      </Box>

      <AvisoCertificadoLogin />

      <DialogPefilFoto
        id={idUser}
        img={fotoPerfil}
        value=""
        open={openFoto}
        onClose={() => setOpenFoto(false)}
      />

      <DialogPefil
        id={idUser}
        value=""
        open={openPerfil}
        onClose={() => setOpenPerfil(false)}
      />
    </>
  );
};
